import { APP_VERSION, DASHBOARD_VERSION_HEADER } from '../appVersion';
import { viewAsHeaders } from '../utils/viewAs';
export const companyDashboardEnabled = () => process.env.REACT_APP_COMPANY_DASHBOARD_ENABLED === 'true';
const KEY = 'companySelectedWash';
const HOME_CHOSEN = 'companyHomeChosen';
const validId = value => typeof value === 'string' && /^[A-Za-z0-9_-]{1,200}$/.test(value);
const COMPANY_ACCESS_BACKOFF_THRESHOLD = 2;
const COMPANY_ACCESS_BACKOFF_MS = 60 * 1000;
const companyAccessBackoff = new Map();

function companyAccessBackoffError(path) {
  const state = companyAccessBackoff.get(path);
  if (!state || state.blockedUntil <= Date.now()) {
    if (state) companyAccessBackoff.delete(path);
    return null;
  }
  const error = new Error(`Authentication backoff active for /company-access${path}`);
  error.code = 'AUTH_BACKOFF';
  error.authBackoffUntil = state.blockedUntil;
  return error;
}

function recordCompanyAccessUnauthorized(path) {
  const previous = companyAccessBackoff.get(path) || { consecutive401s: 0 };
  const consecutive401s = previous.consecutive401s + 1;
  const blockedUntil = consecutive401s >= COMPANY_ACCESS_BACKOFF_THRESHOLD
    ? Date.now() + COMPANY_ACCESS_BACKOFF_MS
    : 0;
  companyAccessBackoff.set(path, { consecutive401s, blockedUntil });
  return blockedUntil > Date.now();
}

export function companySelection(uid) {
  if (!companyDashboardEnabled()) return null;
  const raw = sessionStorage.getItem(KEY);
  if (!raw) return null;
  const selection = JSON.parse(raw);
  if (selection?.uid !== uid) {
    sessionStorage.removeItem(KEY);
    return null;
  }
  if (!validId(selection.orgId)) throw new Error('Invalid company selection');
  return selection;
}

export function companyScopeHeaders(uid) {
  const selection = companySelection(uid);
  return selection ? { 'x-company-org-id': selection.orgId } : {};
}

export function clearCompanySelection() {
  sessionStorage.removeItem(KEY);
  sessionStorage.removeItem(HOME_CHOSEN);
}

// SH6 piece 6: the server suggests opening the only place a person can go when their own account has no sites.
// Followed once per sign-in; never after the person chose their own account in this session.
export function autoOpenTarget(uid, options) {
  if (!companyDashboardEnabled() || !validId(uid) || sessionStorage.getItem(HOME_CHOSEN) === uid || companySelection(uid)) {
    return null;
  }
  const id = options?.autoOpenOrgId;
  return validId(id) && options.organizations?.some(org => org.id === id && org.direct !== true) ? id : null;
}

// Full navigation discards pending responses and every mounted page/provider cache.
// Preserve the existing authenticated session; switching washes must not create a login.
export function prepareCompanySwitch(uid, orgId, direct, clearCache) {
  if (!validId(uid) || !validId(orgId)) throw new Error('Invalid company selection');
  const sessionId = sessionStorage.getItem('sessionId');
  clearCache();
  for (const key of ['selectedOrgId', 'selectedLocationId']) localStorage.removeItem(key);
  for (const key of ['developerSelectedOrgId', 'supportSelectedOrgId']) sessionStorage.removeItem(key);
  if (sessionId) sessionStorage.setItem('sessionId', sessionId);
  sessionStorage.setItem('lastAuthenticatedUid', uid);
  if (direct) {
    clearCompanySelection();
    sessionStorage.setItem(HOME_CHOSEN, uid);
  }
  else sessionStorage.setItem(KEY, JSON.stringify({ uid, orgId }));
}

export function reportCompanyAccessFailure(data) {
  if (data?.code === 'COMPANY_ACCESS_DENIED') window.dispatchEvent(new Event('company-access-denied'));
}

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

// Login creates the server session immediately after Firebase sign-in. The
// Firebase auth listener can run first, so wait briefly before making the
// optional company-access request. This avoids a predictable 401 during the
// normal login race.
export async function waitForServerSession(timeoutMs = 3000) {
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    const sessionId = sessionStorage.getItem('sessionId');
    if (sessionId) return sessionId;
    await wait(50);
  }
  return sessionStorage.getItem('sessionId');
}

// The server keeps the newest two sessions per account; this tab's session was
// evicted by a later login. No retry can succeed, and the caller must end the
// Firebase session too, or the auth listener re-runs discovery from /login forever.
export function isSessionEvicted(error) {
  return error?.code === 'LOGGED_IN_ELSEWHERE';
}

export async function fetchCompanyAccess(user, orgId, options = {}) {
  const base = process.env.REACT_APP_API_URL;
  const path = orgId ? `/context?orgId=${encodeURIComponent(orgId)}` : '/options';
  const initialSessionId = sessionStorage.getItem('sessionId') || 'no-session';
  const backoffKey = `${user?.uid || 'anonymous'}:${initialSessionId}:${path}`;
  const backoffError = companyAccessBackoffError(backoffKey);
  if (backoffError) throw backoffError;
  const retryCount = Number.isSafeInteger(options.retries) ? Math.max(0, options.retries) : 0;
  let lastError;
  for (let attempt = 0; attempt <= retryCount; attempt += 1) {
    // Never force a token refresh here. A forced refresh fires onIdTokenChanged,
    // which re-runs the auth bootstrap that called us: an unbounded loop for as
    // long as the server keeps answering 401 (seen live 2026-09-19..22).
    const viewAs = viewAsHeaders(); // developer View as: the viewed login's company view
    const headers = { Authorization: `Bearer ${await user.getIdToken()}`, [DASHBOARD_VERSION_HEADER]: APP_VERSION, ...viewAs };
    // The API does not check the session id for View as (it is the developer's own), so do not wait for one.
    const sessionId = viewAs['x-view-as-uid'] ? sessionStorage.getItem('sessionId')
      : await waitForServerSession(options.sessionWaitMs ?? 3000);
    if (!sessionId && !viewAs['x-view-as-uid']) {
      const error = new Error('Server session is not ready');
      error.code = 'SESSION_NOT_READY';
      throw error;
    }
    if (sessionId) headers['x-session-id'] = sessionId;
    const response = await fetch(`${base}/company-access${path}`, { headers, cache: 'no-store' });
    if (response.ok) {
      companyAccessBackoff.delete(backoffKey);
      return response.json();
    }
    let data = null;
    try { data = await response.json(); } catch { data = null; }
    const error = new Error(data?.error || 'Company access could not be verified');
    error.status = response.status;
    error.code = data?.code;
    error.entitlement = data?.entitlement;
    lastError = error;
    if (isSessionEvicted(error) || response.status !== 401) throw error;
    if (recordCompanyAccessUnauthorized(backoffKey)) {
      error.authBackoff = true;
      error.authBackoffUntil = companyAccessBackoff.get(backoffKey).blockedUntil;
      throw error;
    }
    if (attempt >= retryCount) throw error;
    await wait(250 * (attempt + 1));
  }
  throw lastError || new Error('Company access could not be verified');
}
